/* eslint-disable react/no-unescaped-entities */
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { FooterStyled, SocialIconButton } from "./style";
import { Link, useLocation } from "react-router-dom";
import { clientContactData, footerData } from "common/websiteData";
import { CONFIG, PAGES_CONTENT } from "config";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import FacebookIcon from "assets/icons/FacebookIcon";
import XIcon from "assets/icons/XIcon";
import InstagramIcon from "assets/icons/InstagramIcon";
import YouTubeIcon from "assets/icons/YouTubeIcon";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const Footer = () => {
  const { pathname } = useLocation();
  const logoWhite = CONFIG.logoWhite;
  const { isAuthenticated } = useSelector((state) => state.auth);

  const JoinMsz = !isAuthenticated ? (
    <>
      <Typography
        mb={1}
        component="h2"
        fontWeight={700}
        fontSize={{ xs: 28, md: 30, lg: 32 }}
        color="text.main"
        className="footer-promo-heading"
      >
        We Welcome Your <span className="accent">Passion</span> And{" "}
        <span className="accent">Expertise</span>
      </Typography>
      <Typography
        mb={2}
        component="p"
        variant="body2"
        fontSize={{ xs: 16, md: 18, lg: 20 }}
        color="text.light"
      >
        Join our empowering sports community today and grow with us.
      </Typography>
      <Button
        component={Link}
        to="/sign-up"
        size="large"
        variant="outlined"
        color="primary"
        className="footer-promo-btn"
        startIcon={<i className="feather-user-plus" />}
      >
        Join With Us
      </Button>
    </>
  ) : (
    <>
      <Typography
        mb={1}
        component="h2"
        fontWeight={700}
        fontSize={{ xs: 28, md: 30, lg: 32 }}
        color="text.main"
        className="footer-promo-heading"
      >
        Welcome back! We're thrilled to have you with us.
      </Typography>
      <Typography
        mb={2}
        component="p"
        variant="body2"
        fontSize={{ xs: 16, md: 18, lg: 20 }}
        color="text.light"
      >
        Your passion and expertise enrich our sports community. Let's continue
        to grow together.
      </Typography>
      <Button
        component={Link}
        to="/dashboard"
        size="large"
        variant="outlined"
        color="primary"
        className="footer-promo-btn"
        startIcon={<DashboardCustomizeOutlinedIcon />}
      >
        Go to Dashboard
      </Button>
    </>
  );

  const privacyLink = footerData.supportLinks?.find((l) =>
    (l.label || "").toLowerCase().includes("privacy"),
  );
  const termsLink = footerData.supportLinks?.find((l) =>
    (l.label || "").toLowerCase().includes("terms"),
  );

  return (
    <FooterStyled component="footer">
      {![
        "/school-fund-raising-program",
        "/badminton-beginners-programs",
        "/pa-day",
        "/winter-camp",
      ].includes(pathname) && (
        <Box className="footer-promo">
          <Container>
            <Box
              maxWidth={744}
              mx="auto"
              className="footer-join aos"
              data-aos="fade-up"
            >
              {JoinMsz}
            </Box>
          </Container>
        </Box>
      )}
      <div className="footer-top">
        <Container>
          <Grid
            container
            className="footer-columns"
            columnSpacing={{ xs: 2, md: 3 }}
            rowSpacing={3}
          >
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <Box component={Link} to="/" className="footer-logo">
                <img
                  width="200"
                  src={logoWhite}
                  alt={CONFIG.websiteName}
                  title={CONFIG.websiteName}
                />
              </Box>
              <Typography component="p" className="footer-desc">
                {PAGES_CONTENT.footer.text.description}
              </Typography>
              <Box mt={1.5} className="contact-block">
                <Typography component="p" variant="body2" gutterBottom>
                  Need Live Support
                </Typography>
                <Typography
                  component={Link}
                  to={clientContactData.email.href}
                  className="nav-link"
                >
                  {clientContactData.email.label}
                </Typography>
              </Box>
              <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                <SocialIconButton
                  className="facebook"
                  component={Link}
                  to={clientContactData.social.facebook}
                  target="_blank"
                >
                  <FacebookIcon />
                </SocialIconButton>
                <SocialIconButton
                  className="twitter"
                  component={Link}
                  to={clientContactData.social.twitter}
                  target="_blank"
                >
                  <XIcon />
                </SocialIconButton>
                <SocialIconButton
                  className="instagram"
                  component={Link}
                  to={clientContactData.social.instagram}
                  target="_blank"
                >
                  <InstagramIcon />
                </SocialIconButton>
                <SocialIconButton
                  className="youtube"
                  component={Link}
                  to={clientContactData.social.youtube}
                  target="_blank"
                >
                  <YouTubeIcon />
                </SocialIconButton>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={2}>
              <Typography component="h4" className="footer-col-heading">
                Quick Links
              </Typography>
              {footerData.quickLinks?.map(({ href, label }, i) => (
                <Box key={`quickLinks_${i}`} className="nav-item">
                  <Typography
                    component={Link}
                    to={href}
                    fontSize={16}
                    className="nav-link"
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={2}>
              <Typography component="h4" className="footer-col-heading">
                Support
              </Typography>
              {footerData.supportLinks?.map(({ href, label }, i) => (
                <Box key={`supportLinks${i}`} className="nav-item">
                  <Typography
                    component={Link}
                    to={href}
                    fontSize={16}
                    className="nav-link"
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={2}>
              <Typography component="h4" className="footer-col-heading">
                Other Links
              </Typography>
              {footerData[
                isAuthenticated ? "otherAuthLinks" : "otherLinks"
              ]?.map(({ href, label }, i) => (
                <Box key={`otherLinks${i}`} className="nav-item">
                  <Typography
                    component={Link}
                    to={href}
                    fontSize={16}
                    className="nav-link"
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={3}>
              <Typography component="h4" className="footer-col-heading">
                Contact Us
              </Typography>
              <Box className="contact-item">
                <LocationOnRoundedIcon className="contact-icon" />
                <Typography
                  component="p"
                  variant="body2"
                  className="contact-text"
                >
                  {clientContactData.address}
                </Typography>
              </Box>
              <Box className="contact-item">
                <LocationOnRoundedIcon className="contact-icon" />
                <Typography
                  component="p"
                  variant="body2"
                  className="contact-text"
                >
                  {clientContactData.address2}
                </Typography>
              </Box>
              <Box className="contact-item">
                <LocationOnRoundedIcon className="contact-icon" />
                <Typography
                  component="p"
                  variant="body2"
                  className="contact-text"
                >
                  {clientContactData.address3}
                </Typography>
              </Box>

              <Box mt={1.5} className="contact-block">
                <Typography component="p" variant="body2" gutterBottom>
                  Call Us
                </Typography>
                <Typography
                  component={Link}
                  to={clientContactData.phoneNo.href}
                  className="nav-link"
                  display="block"
                >
                  {clientContactData.phoneNo.label}
                </Typography>
                <Typography
                  component={Link}
                  to={clientContactData.phoneNo2.href}
                  className="nav-link"
                  display="block"
                >
                  {clientContactData.phoneNo2.label}
                </Typography>
                <Typography
                  component={Link}
                  to={clientContactData.phoneNo3.href}
                  className="nav-link"
                  display="block"
                >
                  {clientContactData.phoneNo3.label}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </div>

      <div className="footer-bottom">
        <Container>
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            alignItems={{ xs: "center", md: "center" }}
            justifyContent="space-between"
            gap={1}
          >
            <Typography
              component="p"
              variant="body2"
              className="footer-copyright"
            >
              © {dayjs().year()} {CONFIG.websiteName} - All rights reserved.
            </Typography>
            <Box display="flex" gap={2}>
              {privacyLink && (
                <Typography
                  component={Link}
                  to={privacyLink.href}
                  variant="body2"
                  className="footer-legal-link"
                >
                  Privacy Policy
                </Typography>
              )}
              {termsLink && (
                <Typography
                  component={Link}
                  to={termsLink.href}
                  variant="body2"
                  className="footer-legal-link"
                >
                  Terms & Conditions
                </Typography>
              )}
            </Box>
          </Box>
        </Container>
      </div>
    </FooterStyled>
  );
};

export default Footer;
